import { useRef } from "preact/hooks";

/** How far a row must travel, as a share of its width, before letting go commits the action. */
const COMMIT = 0.32;

type SwipeHandlers = { onLeft: (() => void) | undefined; onRight: (() => void) | undefined };

/**
 * Swipe actions for touch screens, as in a phone's mail app: drag a row left or right and
 * let go past the threshold to run the action. Mouse and pen input is ignored, so dragging
 * rows to folders on a desktop keeps working. The row gets `--swipe` (its offset) and a
 * data-swipe attribute naming the side, for the CSS that draws the action underneath.
 */
export const useSwipe = ({ onLeft, onRight }: SwipeHandlers) => {
  const start = useRef<{ x: number; y: number; id: number; locked: boolean | null } | null>(null);
  const moved = useRef(false);

  const reset = (row: HTMLElement, animate: boolean) => {
    row.classList.toggle("swipe-settle", animate);
    row.style.setProperty("--swipe", "0px");
    row.removeAttribute("data-swipe");
    if (animate) setTimeout(() => row.classList.remove("swipe-settle", "swiping"), 200);
    else row.classList.remove("swiping");
  };

  return {
    onPointerDown: (event: PointerEvent) => {
      if (event.pointerType !== "touch" || (!onLeft && !onRight)) return;
      start.current = { x: event.clientX, y: event.clientY, id: event.pointerId, locked: null };
      moved.current = false;
    },
    onPointerMove: (event: PointerEvent) => {
      const s = start.current;
      if (!s || event.pointerId !== s.id) return;
      const row = event.currentTarget as HTMLElement;
      const dx = event.clientX - s.x;
      const dy = event.clientY - s.y;
      // Decide once whether this gesture is a horizontal swipe or a vertical scroll.
      if (s.locked === null) {
        if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
        s.locked = Math.abs(dx) > Math.abs(dy) * 1.2;
        if (!s.locked) {
          start.current = null;
          return;
        }
        row.setPointerCapture(event.pointerId);
        row.classList.add("swiping");
      }
      const side = dx < 0 ? "left" : "right";
      const allowed = side === "left" ? onLeft : onRight;
      const offset = allowed ? dx : dx / 6;
      moved.current = true;
      row.style.setProperty("--swipe", `${offset}px`);
      row.dataset.swipe = side;
      row.classList.toggle("swipe-ready", !!allowed && Math.abs(dx) > row.offsetWidth * COMMIT);
    },
    onPointerUp: (event: PointerEvent) => {
      const s = start.current;
      start.current = null;
      if (!s?.locked) return;
      const row = event.currentTarget as HTMLElement;
      const dx = event.clientX - s.x;
      const action = dx < 0 ? onLeft : onRight;
      const committed = !!action && Math.abs(dx) > row.offsetWidth * COMMIT;
      row.classList.remove("swipe-ready");
      if (committed && dx < 0) {
        // Leaving actions slide the row out before it disappears from the list.
        row.classList.add("swipe-settle");
        row.style.setProperty("--swipe", `${-row.offsetWidth}px`);
        setTimeout(() => {
          action?.();
          reset(row, false);
        }, 180);
      } else {
        if (committed) action?.();
        reset(row, true);
      }
    },
    onPointerCancel: (event: PointerEvent) => {
      start.current = null;
      reset(event.currentTarget as HTMLElement, true);
    },
    /** True right after a swipe, so the click that follows does not open the message. */
    swallowClick: () => {
      const was = moved.current;
      moved.current = false;
      return was;
    },
  };
};

/**
 * Swipe from the left edge to go back, as on iPhone: a touch that starts within the first
 * 28px and drags right drags the open conversation along, and letting go past 90px closes it.
 */
export const useEdgeBack = (onBack: () => void) => {
  const start = useRef<{ x: number; id: number } | null>(null);
  return {
    onPointerDown: (event: PointerEvent) => {
      if (event.pointerType !== "touch" || event.clientX > 28) return;
      start.current = { x: event.clientX, id: event.pointerId };
      (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
    },
    onPointerMove: (event: PointerEvent) => {
      const s = start.current;
      if (!s || event.pointerId !== s.id) return;
      const pane = event.currentTarget as HTMLElement;
      pane.classList.add("edge-back");
      pane.style.setProperty("--swipe", `${Math.max(0, event.clientX - s.x)}px`);
    },
    onPointerUp: (event: PointerEvent) => {
      const s = start.current;
      start.current = null;
      if (!s) return;
      const pane = event.currentTarget as HTMLElement;
      pane.classList.remove("edge-back");
      pane.style.removeProperty("--swipe");
      if (event.clientX - s.x > 90) onBack();
    },
    onPointerCancel: (event: PointerEvent) => {
      start.current = null;
      const pane = event.currentTarget as HTMLElement;
      pane.classList.remove("edge-back");
      pane.style.removeProperty("--swipe");
    },
  };
};
